import {
  Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild, ViewEncapsulation,
} from '@angular/core';

export interface FsComboOption {
  id: string;
  name: string;
}

/**
 * Searchable single-select combobox — the pattern first built for the invitation wizard's
 * organization picker, lifted out so anything can use it.
 *
 * Keyboard-complete (arrows / Enter / Escape / click-outside) and ARIA-labelled, because a
 * custom dropdown that only works with a mouse is a regression on the <select> it replaces.
 *
 * Takes options and a value; emits selections. It owns no data and knows nothing about orgs.
 */
@Component({
  selector: 'app-fs-combobox',
  templateUrl: './fs-combobox.component.html',
  styleUrls: ['./fs-combobox.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class FsComboboxComponent {
  @Input() options: FsComboOption[] = [];
  @Input() value: string | null = null;
  @Input() placeholder = 'Select…';
  @Input() searchPlaceholder = 'Search…';
  @Input() emptyText = 'Nothing matches';
  @Input() ariaLabel = '';

  /** Set to offer an inline "create" action in the empty state; the host does the creating. */
  @Input() createLabel = '';

  @Output() valueChange = new EventEmitter<string>();
  @Output() selectedOption = new EventEmitter<FsComboOption>();
  @Output() create = new EventEmitter<string>();
  /** Fires when the menu closes — hosts use it to mark a form control touched. */
  @Output() closed = new EventEmitter<void>();

  @ViewChild('combo') combo?: ElementRef<HTMLElement>;
  @ViewChild('search') search?: ElementRef<HTMLInputElement>;

  open = false;
  filter = '';
  activeIndex = 0;

  get selectedName(): string {
    return this.options.find(o => o.id === this.value)?.name ?? '';
  }

  get filtered(): FsComboOption[] {
    const q = this.filter.trim().toLowerCase();
    return q ? this.options.filter(o => o.name.toLowerCase().includes(q)) : this.options;
  }

  /**
   * Track options by id so the rows survive change detection.
   *
   * Hosts often bind [options] to a getter (e.g. `get orgOptions() { return orgs.map(...) }`),
   * which hands us a new array of new object references every CD. Without this trackBy, *ngFor
   * would destroy and rebuild every <li> on each CD — and because (mouseenter) triggers CD while
   * the pointer is down, the option node gets replaced between mousedown and mouseup, so the
   * browser never fires `click` and selection silently fails. Keying on the stable id keeps the
   * DOM nodes, so clicks land.
   */
  trackById(_index: number, option: FsComboOption): string { return option.id; }

  openMenu(): void {
    this.open = true;
    this.filter = '';
    this.activeIndex = Math.max(0, this.filtered.findIndex(o => o.id === this.value));
    setTimeout(() => this.search?.nativeElement.focus());
  }

  closeMenu(): void {
    this.open = false;
    this.closed.emit();
  }

  onFilter(value: string): void {
    this.filter = value;
    this.activeIndex = 0;
  }

  select(o: FsComboOption): void {
    this.value = o.id;
    this.valueChange.emit(o.id);
    this.selectedOption.emit(o);
    this.open = false;
    this.filter = '';
  }

  onKeydown(event: KeyboardEvent): void {
    if (!this.open && (event.key === 'ArrowDown' || event.key === 'Enter')) {
      this.openMenu();
      event.preventDefault();
      return;
    }
    if (!this.open) { return; }
    const list = this.filtered;
    switch (event.key) {
      case 'ArrowDown':
        this.activeIndex = Math.min(this.activeIndex + 1, list.length - 1);
        event.preventDefault();
        break;
      case 'ArrowUp':
        this.activeIndex = Math.max(this.activeIndex - 1, 0);
        event.preventDefault();
        break;
      case 'Enter':
        if (list[this.activeIndex]) { this.select(list[this.activeIndex]); }
        event.preventDefault();
        break;
      case 'Escape':
        this.closeMenu();
        event.preventDefault();
        break;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open) { return; }
    if (!this.combo?.nativeElement.contains(event.target as Node)) { this.closeMenu(); }
  }
}
